import { Check } from "lucide-react";
import { Reveal, Kicker } from "../lib/motion";

const TIERS = [
  {
    name: "Essentials",
    for: "For early-stage and smaller businesses",
    text: "Core finance, accounting and compliance support — ideal for businesses building a professional finance foundation.",
    features: ["Accounting & monthly reporting", "Statutory compliance", "Payroll coordination", "Finance foundation setup"],
    featured: false,
  },
  {
    name: "Growth",
    for: "For scaling and funded businesses",
    text: "Fractional CFO leadership plus the planning, reporting and process muscle a scaling company needs.",
    features: ["Fractional CFO leadership", "FP&A & management reporting", "Cash-flow management", "Investor reporting", "Finance-process development"],
    featured: true,
  },
  {
    name: "Enterprise",
    for: "For GCCs, larger companies and complex groups",
    text: "Full controllership, cross-border finance and transformation programmes for complex operating environments.",
    features: ["Full controllership", "Multi-entity reporting", "Cross-border finance", "Finance Shared Services", "Treasury & internal controls", "Finance transformation"],
    featured: false,
  },
];

export const Engagement = ({ onConsult }) => (
  <section data-testid="engagement-section" className="bg-mist py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal className="max-w-2xl">
        <Kicker>Engagement Model</Kicker>
        <h2 className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tight text-navy">
          CFO support at the level you need.
        </h2>
        <p className="mt-5 text-base md:text-lg leading-relaxed text-body">
          From a few days of senior CFO support each month to full controllership,
          shared services and transformation engagements. Custom scopes available.
        </p>
        <p className="mt-4 text-sm font-semibold text-navy">
          Every engagement is tailored to scope, complexity and the level of finance leadership required.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3 md:items-stretch">
        {TIERS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.12} className="h-full">
            <article
              data-testid={`tier-${t.name.toLowerCase()}`}
              className={`relative flex h-full flex-col rounded-2xl p-8 md:p-9 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 ${
                t.featured
                  ? "bg-navy text-white shadow-[0_32px_70px_rgba(10,31,60,0.35)] md:-my-5 md:py-14"
                  : "border border-border bg-white hover:shadow-[0_20px_50px_rgba(10,31,60,0.10)]"
              }`}
            >
              <p className={`font-editorial italic text-lg ${t.featured ? "text-brand" : "text-brand-hover"}`}>
                {t.for}
              </p>
              <h3 className={`mt-2 font-display text-3xl font-extrabold tracking-tight ${t.featured ? "text-white" : "text-navy"}`}>
                {t.name}
              </h3>
              <p className={`mt-4 text-sm md:text-base leading-relaxed ${t.featured ? "text-white/65" : "text-body"}`}>
                {t.text}
              </p>
              <ul className="mt-7 flex-1 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm font-medium ${t.featured ? "text-white/85" : "text-navy/80"}`}>
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${t.featured ? "bg-brand/20" : "bg-brand/15"}`}>
                      <Check size={11} strokeWidth={3.5} className="text-brand-hover" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`tier-cta-${t.name.toLowerCase()}`}
                onClick={onConsult}
                className={`mt-8 w-full rounded-full py-3.5 text-sm font-bold transition-[transform,background-color,color] duration-300 hover:scale-[1.02] active:scale-[0.98] ${
                  t.featured
                    ? "bg-brand text-navy hover:bg-brand-hover hover:text-white"
                    : "border border-navy/20 text-navy hover:bg-navy hover:text-white"
                }`}
              >
                Book a Consultation
              </button>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
